
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import AlertBanner from '../components/AlertBanner';
import TravelAlertsSection from '../components/TravelAlertsSection';
import InquiryForm from '../components/InquiryForm';
import ServicesSlider from '../components/ServicesSlider';
import api from '../api';
import useLang from '../hooks/useLang';
import HeroSlider from '../components/HeroSlider';
import PartnersSection from '../components/PartnersSection';


const services = [
  { name: 'Flight Booking', slug: 'flight-booking', icon: '✈️', blurb: 'Budget-friendly fares across global routes.' },
  { name: 'Hotel Booking', slug: 'hotel-booking', icon: '🏨', blurb: 'Luxury and comfortable stays, handpicked.' },
  { name: 'Holiday Packages', slug: 'holiday-packages', icon: '🧳', blurb: 'Custom itineraries built around you.' },
  { name: 'Visa Assistance', slug: 'visa-assistance', icon: '🛂', blurb: 'Documentation support, simplified.' },
  { name: 'Travel Insurance', slug: 'travel-insurance', icon: '🛡️', blurb: 'Travel with peace of mind.' },
  { name: 'Airport Transfer', slug: 'airport-transfer', icon: '🚗', blurb: 'Seamless pickups and drop-offs.' },
  { name: 'Cruise Packages', slug: 'cruise-packages', icon: '🚢', blurb: 'Set sail on curated cruise routes.' },
  { name: 'Corporate Travel', slug: 'corporate-travel-services', icon: '💼', blurb: 'Reliable travel management for teams.' }
];

export default function Home() {
  const { t } = useTranslation();
  const [testimonials, setTestimonials] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [hoveredDestination, setHoveredDestination] = useState(null);
  var { pick } = useLang();

  useEffect(() => {
    api.get('/testimonials').then((r) => setTestimonials(r.data)).catch(() => {});
    api.get('/destinations').then((r) => setDestinations(r.data.slice(0, 6))).catch(() => {});
    api.post('/stats/pageview').catch(() => {});
  }, []);

  return (
    <Layout>
      <AlertBanner type="offer" />

      {/* HERO */}
      <HeroSlider/>


      {/* INTRO */}
      <section className="section">
        <div className="container" style={{ maxWidth: 720, textAlign: 'center', margin: '0 auto' }}>
          <span className="eyebrow">Who we are</span>
          <h2>Travel solutions, planned with care.</h2>
          <p>
            Bluejay Travels is a full-service travel agency based in Dakar, Senegal, helping travellers
            across the world book budget-friendly flights, secure luxury hotel stays, and design custom holiday
            packages — all with the same attention a bluejay gives to charting its migratory path: deliberate,
            unhurried, and always arriving exactly where it means to.
          </p>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
        <ServicesSlider />

        <PartnersSection />

      {/* DESTINATIONS TEASER */}
      {/* DESTINATIONS */}
{destinations.length > 0 && (
  <section className="section">
    <div className="container">

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: 36,
          flexWrap: "wrap",
          gap: 16
        }}
      >
        <div>
          <span className="eyebrow">Popular destinations</span>
          <h2 style={{ marginTop: 8 }}>
            Where the path leads next
          </h2>
        </div>

        <Link
          to="/destinations"
          className="btn btn-outline"
        >
          View all destinations →
        </Link>
      </div>

      <div className="grid-3">

        {destinations.map((d) => {

          const hovered = hoveredDestination === d.slug;

          return (

            <Link
              key={d.slug}
              to={`/destinations/${d.slug}`}
              style={{
                textDecoration: "none",
                display: "block"
              }}
              onMouseEnter={() => setHoveredDestination(d.slug)}
              onMouseLeave={() => setHoveredDestination(null)}
            >

              <div
                style={{
                  position: "relative",
                  borderRadius: 16,
                  overflow: "hidden",
                  aspectRatio: "4 / 3",
                  boxShadow: hovered
                    ? "0 20px 48px rgba(17,45,78,0.22)"
                    : "0 6px 24px rgba(17,45,78,0.10)",
                  transform: hovered
                    ? "translateY(-6px)"
                    : "translateY(0)",
                  transition:
                    "transform .35s ease, box-shadow .35s ease"
                }}
              >

                <img
                  src={
                    d.heroImage ||
                    "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=800&q=80"
                  }
                  alt={d.name}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transform: hovered
                      ? "scale(1.07)"
                      : "scale(1)",
                    transition: "transform .5s ease"
                  }}
                />

                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: hovered
                      ? "linear-gradient(to top, rgba(17,45,78,.88) 0%, rgba(17,45,78,.4) 50%, rgba(17,45,78,.05) 100%)"
                      : "linear-gradient(to top, rgba(17,45,78,.72) 0%, rgba(17,45,78,.15) 60%, transparent 100%)",
                    transition: "background .35s ease"
                  }}
                />

                <div
                  style={{
                    position: "absolute",
                    top: 14,
                    left: 14,
                    background: "rgba(255,255,255,.18)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(255,255,255,.25)",
                    borderRadius: 20,
                    padding: "4px 12px",
                    fontSize: ".72rem",
                    fontWeight: 700,
                    color: "#fff",
                    letterSpacing: ".06em",
                    textTransform: "uppercase"
                  }}
                >
                  {d.country}
                </div>

                <div
                  style={{
                    position: "absolute",
                    top: 14,
                    right: 14,
                    background: "var(--bj-clay)",
                    borderRadius: 20,
                    padding: "4px 12px",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: ".72rem",
                    opacity: hovered ? 1 : 0,
                    transform: hovered
                      ? "translateY(0)"
                      : "translateY(-6px)",
                    transition:
                      "opacity .3s ease, transform .3s ease"
                  }}
                >
                  Explore →
                </div>

                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: "20px 18px 18px"
                  }}
                >
                  <h3
                    style={{
                      margin: "0 0 4px",
                      color: "#fff",
                      fontFamily: "var(--font-display)",
                      textShadow: "0 2px 8px rgba(0,0,0,.3)"
                    }}
                  >
                    {d.name}
                  </h3>

                  {d.overview && (
                    <p
                      style={{
                        margin: 0,
                        color: "rgba(255,255,255,.78)",
                        fontSize: ".82rem",
                        lineHeight: 1.5,
                        maxHeight: hovered ? 60 : 0,
                        overflow: "hidden",
                        opacity: hovered ? 1 : 0,
                        transition:
                          "max-height .35s ease, opacity .35s ease"
                      }}
                    >
                      {d.overview.slice(0, 90)}...
                    </p>
                  )}
                </div>

              </div>

            </Link>

          );
        })}

      </div>

    </div>
  </section>
)}

      <TravelAlertsSection />

      {/* QUICK INQUIRY */}
      <section className="section" style={{ background: 'var(--bj-espresso)' }}>
        <div className="container two-col">
          <div style={{ color: 'var(--bj-cream)' }}>
            <span className="eyebrow" style={{ color: 'var(--bj-sand)' }}>Quick inquiry</span>
            <h2 style={{ color: 'var(--bj-cream)' }}>Tell us where you're headed.</h2>
            <p style={{ opacity: 0.85 }}>One short form — we'll route it to the right specialist and confirm by email right away.</p>
          </div>
          <InquiryForm defaultType="general" compact />
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="section">
          <div className="container">
            <span className="eyebrow">In their words</span>
            <h2 style={{ marginBottom: 32 }}>Customer testimonials</h2>
            <div className="grid-3">
              {testimonials.slice(0, 3).map((t) => (
                <div key={t._id} className="card" style={{ padding: 24 }}>
                  <p style={{ fontStyle: 'italic' }}>"{pick(t, 'message')}"</p>
                  <p style={{ fontWeight: 700, marginTop: 12, marginBottom: 0 }}>{t.customerName}</p>
                  <p style={{ fontSize: '0.85rem', opacity: 0.7 }}>{t.location}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
